import { useContext, useState } from 'react';
import { Divider, Grid, Stack, Button, TextField, IconButton, Box, InputAdornment, FormControl, InputLabel as MuiInputLabel, Select, MenuItem } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import InputLabel from 'src/berry/ui-component/extended/Form/InputLabel.jsx';
import { gridSpacing } from 'src/berry/store/constant.jsx';
import CONFIG from 'src/config.json';

import ModalsContext from 'src/context/modals.jsx';
import MainCard from 'src/berry/ui-component/cards/MainCard';
import FileSelector from 'src/components/file_selector/component.jsx';

const CoordinatesSelector = ({ close, marker_position, era_id, handle_coordinate_selection }) => {
	const bkg_path = `/img/eras/${era_id}/bkg.jpg`;
	const roads_path = `/img/eras/${era_id}/roads.png`;
	const cities_path = `/img/eras/${era_id}/cities.png`;

	return (
		<MainCard
			sx={{
				'position': 'fixed',
				'top': '50%',
				'right': '50%',
				'transform': 'translate(50%, -50%)',
				'zIndex': 1201,
				'width': 'calc(100vw - 50px)',
				'height': 'calc(100vh - 50px)',
				'boxShadow': '10px 2px 100px rgb(0, 0, 0, 50%)',
				'mt': '0 !important',
				'&:hover': {
					boxShadow: '10px 2px 100px rgb(0, 0, 0, 50%)',
				},
			}}
			title={`Choose Coordinates`}
			secondary={
				<IconButton onClick={close} size="large">
					<CloseIcon fontSize="small" />
				</IconButton>
			}
		>
			<Grid container spacing={gridSpacing} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', margin: 0, width: '100%' }}>
				<Box onClick={handle_coordinate_selection} sx={{ position: 'relative', cursor: 'crosshair', display: 'inline-block' }}>
					<img src={bkg_path} alt="Era Background" style={{ maxWidth: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 1 }} />
					<img src={roads_path} alt="Era Roads" style={{ maxWidth: '100%', height: 'auto', display: 'block', position: 'absolute', top: 0, left: 0, zIndex: 2, pointerEvents: 'none' }} />
					<img src={cities_path} alt="Era Cities" style={{ maxWidth: '100%', height: 'auto', display: 'block', position: 'absolute', top: 0, left: 0, zIndex: 3, pointerEvents: 'none' }} />

					{marker_position && (
						<Box
							position="absolute"
							top={`${marker_position.y}%`}
							left={`${marker_position.x}%`}
							sx={{ border: '1px solid #00000080', backgroundColor: 'white', transform: 'translate3d(-50%, -50%, 0px)', zIndex: 4 }}
							width="43px"
							height="43px"
							borderRadius="50%"
						/>
					)}
				</Box>
			</Grid>
		</MainCard>
	);
};

const ContentItem = ({ control, index, content_items, setValue, watch, on_remove }) => {
	const item = content_items?.[index] || { type: 'text', data: '' };
	const item_type = watch(`content[${index}].type`) || item.type;
	const item_data = watch(`content[${index}].data`) || item.data;

	return (
		<>
			<Grid item xs={12}>
				<Stack direction="row" spacing={2} alignItems="flex-start">
					<Grid container spacing={gridSpacing} sx={{ flex: 1 }}>
						<Grid item xs={12} md={3}>
							<Stack spacing={gridSpacing}>
								<InputLabel>Type</InputLabel>
								<Controller
									name={`content[${index}].type`}
									control={control}
									defaultValue={item.type}
									render={({ field }) => (
										<FormControl fullWidth>
											<MuiInputLabel id={`content-type-${index}`}>Type</MuiInputLabel>
											<Select {...field} labelId={`content-type-${index}`} label="Type">
												<MenuItem value="img">Image</MenuItem>
												<MenuItem value="header">Header</MenuItem>
												<MenuItem value="text">Text</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Stack>
						</Grid>

						<Grid item xs={12} md={9}>
							<Stack spacing={gridSpacing}>
								<InputLabel>Data</InputLabel>
								{item_type === 'img' ? (
									<FileSelector
										on_change={(data) => {
											setValue(`content[${index}].data`, data);
										}}
										value={item_data}
									/>
								) : (
									<Controller
										name={`content[${index}].data`}
										control={control}
										defaultValue={item.data || ''}
										render={({ field }) => <TextField {...field} fullWidth multiline={item_type === 'text' ? true : false} rows={item_type === 'text' ? 4 : 1} />}
									/>
								)}
							</Stack>
						</Grid>
					</Grid>
					<IconButton color="error" onClick={on_remove} sx={{ mt: 4 }}>
						<DeleteIcon />
					</IconButton>
				</Stack>
			</Grid>
			<Grid item xs={12}>
				<Divider />
			</Grid>
		</>
	);
};

const PinForm = ({ action }) => {
	const {
		hide,
		data: { data, clb, era_id },
	} = useContext(ModalsContext);

	const { handleSubmit, control, setValue, watch } = useForm({
		defaultValues: data || { type: 'plus', content: [] },
	});

	const [open_map, set_open_map] = useState(false);
	const [marker_position, set_marker_position] = useState({ x: data?.x, y: data?.y });

	const on_submit = (form_data) => {
		clb(form_data);
		hide();
	};

	const handle_coordinate_selection = (event) => {
		if (event.target.tagName !== 'IMG') return;
		const bkg_img = event.currentTarget.querySelector('img[alt="Era Background"]');
		if (!bkg_img) return;
		const rect = bkg_img.getBoundingClientRect();
		const x = ((event.clientX - rect.left) / rect.width) * 100;
		const y = ((event.clientY - rect.top) / rect.height) * 100;
		set_marker_position({ x, y });
		setValue('x', x);
		setValue('y', y);
	};

	const pin_type = watch('type');
	const content_array = watch('content') || [];

	return (
		<>
			{open_map && (
				<CoordinatesSelector
					close={() => set_open_map(false)}
					marker_position={marker_position}
					era_id={era_id}
					handle_coordinate_selection={handle_coordinate_selection}
				/>
			)}
			<Grid container spacing={gridSpacing} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 250px)', pb: '30px' }}>
				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<InputLabel>Type</InputLabel>
						<Controller
							name="type"
							control={control}
							defaultValue="plus"
							render={({ field }) => (
								<FormControl fullWidth>
									<MuiInputLabel id="pin-type">Type</MuiInputLabel>
									<Select {...field} labelId="pin-type" label="Type">
										<MenuItem value="plus">Plus</MenuItem>
										<MenuItem value="star">Star</MenuItem>
										<MenuItem value="crossroads">Crossroads</MenuItem>
									</Select>
								</FormControl>
							)}
						/>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<InputLabel>Position</InputLabel>
						<Controller
							name="position"
							control={control}
							defaultValue="bottom-right"
							render={({ field }) => (
								<FormControl fullWidth>
									<MuiInputLabel id="pin-position">Position</MuiInputLabel>
									<Select {...field} labelId="pin-position" label="Position">
										<MenuItem value="bottom-right">Bottom Right</MenuItem>
										<MenuItem value="bottom-left">Bottom Left</MenuItem>
										<MenuItem value="top-left">Top Left</MenuItem>
										<MenuItem value="top-right">Top Right</MenuItem>
									</Select>
								</FormControl>
							)}
						/>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Divider />
				</Grid>

				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
							<InputLabel>Coordinates</InputLabel>
							<Button variant="outlined" color="primary" onClick={() => set_open_map(true)} size="small">
								Set coordinates on the map
							</Button>
						</Stack>
						<Stack direction="row" spacing={gridSpacing}>
							<Grid item md={6}>
								<Controller
									name="x"
									control={control}
									defaultValue=""
									render={({ field: { value, onChange } }) => (
										<TextField
											value={value}
											onChange={(e) => {
												onChange(e);
												set_marker_position({ ...marker_position, x: Number(e.target.value) });
											}}
											type="number"
											fullWidth
											label="X"
											InputProps={{
												endAdornment: <InputAdornment position="end">%</InputAdornment>,
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item md={6}>
								<Controller
									name="y"
									control={control}
									defaultValue=""
									render={({ field: { value, onChange } }) => (
										<TextField
											value={value}
											onChange={(e) => {
												onChange(e);
												set_marker_position({ ...marker_position, y: Number(e.target.value) });
											}}
											type="number"
											fullWidth
											label="Y"
											InputProps={{
												endAdornment: <InputAdornment position="end">%</InputAdornment>,
											}}
										/>
									)}
								/>
							</Grid>
						</Stack>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Divider />
				</Grid>

				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<InputLabel>Content</InputLabel>
						{content_array.map((_, index) => (
							<ContentItem
								key={index}
								control={control}
								index={index}
								content_items={watch('content')}
								setValue={setValue}
								watch={watch}
								on_remove={() => {
									const current_content = watch('content') || [];
									const new_content = current_content.filter((_, i) => i !== index);
									setValue('content', new_content);
								}}
							/>
						))}
						<Button
							variant="outlined"
							fullWidth
							startIcon={<AddIcon />}
							onClick={() => {
								const current_content = watch('content') || [];
								const new_item = { type: 'text', data: '' };
								setValue('content', [...current_content, new_item]);
							}}
						>
							Add Item
						</Button>
					</Stack>
				</Grid>
			</Grid>

			<Grid container>
				<Grid item xs={12}>
					<Divider />
				</Grid>
				<Grid item xs={12}>
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: '15px' }}>
						<Button color="error" onClick={hide}>
							Cancel
						</Button>
						<Button variant="contained" size="small" onClick={handleSubmit(on_submit)}>
							{action === 'edit' ? 'Save' : 'Add'}
						</Button>
					</Stack>
				</Grid>
			</Grid>
		</>
	);
};

export default PinForm;

